'use client'

import * as React from 'react'
import * as Dialog from 'primitives/dialog'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useSearchParams, useRouter } from 'next/navigation'
import { IconButton } from 'primitives/buttons/icon-button'
import { Button } from '@repo/design-system-cf/components/button'
import { useIsLoggedInQuery } from 'auth/hooks'

import { CloseIcon } from '@web/app/[locale]/clubhouse/assets/icons'
import {
  useClubhouseUpdateDigestPreferenceMutation,
  useClubhousesGetDetailsQuery,
} from '@web/gen/clubhouses'
import { ClubhouseDigestFrequency } from '@web/gen/types'
import { LFS_URL } from '@web/utils/env'

export function SwitchDigestDialog({ slug }: { slug: string }) {
  const t = useTranslations('pages.clubhouse.unsubscribe_dialog')
  const searchParams = useSearchParams()
  const router = useRouter()
  const [open, setOpen] = React.useState(false)
  const clubhouseUpdateDigestPreferenceMutation = useClubhouseUpdateDigestPreferenceMutation()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { data: clubhouse } = useClubhousesGetDetailsQuery({
    input: { clubhouseSlug: slug },
  })

  // TODO - update with correct search param
  const showModalParam = searchParams.get('modal') === 'digest-unsubscribe'
  const isMember = clubhouse?.clubhousesGetDetails?.clubhouse?.isMember
  const isSubscribedToDigest = clubhouse?.clubhousesGetDetails?.clubhouse?.isSubscribedToDigest

  function handleWeeklyClick() {
    clubhouseUpdateDigestPreferenceMutation.mutate(
      {
        input: {
          clubhouseSlug: slug,
          frequency: ClubhouseDigestFrequency.Weekly,
        },
      },
      {
        onSuccess: data => {
          if (data?.clubhousesUpdateDigestPreference.success) {
            setOpen(false)
            handleClickClose()
          }
        },
      },
    )
  }

  function handleUnsubscribeClick() {
    clubhouseUpdateDigestPreferenceMutation.mutate(
      {
        input: {
          clubhouseSlug: slug,
          frequency: ClubhouseDigestFrequency.Disabled,
        },
      },
      {
        onSuccess: data => {
          if (data?.clubhousesUpdateDigestPreference.success) {
            setOpen(false)
            handleClickClose()
          }
        },
      },
    )
  }

  function handleClickClose() {
    setOpen(false)
    const current = new URLSearchParams(searchParams.toString())
    current.delete('modal')
    router.replace(`?${current.toString()}`, { scroll: false })
  }

  React.useEffect(() => {
    if (showModalParam && isLoggedIn && isMember && isSubscribedToDigest) {
      setOpen(true)
    }
  }, [showModalParam, isLoggedIn, isMember, isSubscribedToDigest])

  return (
    <Dialog.Root open={open} onOpenChange={setOpen} defaultOpen={true}>
      <React.Suspense fallback={null}>
        <Dialog.Portal forceMount>
          <Dialog.Body className="font-poppins xl:overflow-y-auto xl:py-10" zIndex={2000}>
            <div className="relative w-full max-w-[656px]">
              <div className="bg-cf-white rounded-2 relative flex flex-col overflow-y-auto">
                <div className="p-6">
                  <div className="flex w-full items-center justify-center py-3">
                    <Image
                      src={`${LFS_URL}/web/pages/clubhouses/clubhouse-Illustrations-switch.png`}
                      width={200}
                      height={160}
                      alt="Switch to Weekly Digest"
                    />
                  </div>
                  <p className="text-20 text-cf-sf-gray-1050 pb-3 font-bold">{t('title')}</p>
                  <p className="text-16 text-cf-sf-gray-1050 pb-6">{t('description')}</p>
                  <div className="flex items-center justify-center gap-4 md:justify-end">
                    <Dialog.Close asChild>
                      <button
                        onClick={handleUnsubscribeClick}
                        className="text-legacy-cf-purple-100 font-medium hover:text-[#2A1F3F] focus-visible:outline-none"
                      >
                        {t('unsubscribe_button')}
                      </button>
                    </Dialog.Close>
                    <Button
                      onClick={handleWeeklyClick}
                      className="rounded-2 text-14 p-2 font-semibold"
                    >
                      {t('switch_button')}
                    </Button>
                  </div>
                </div>
                <Dialog.Close asChild>
                  <IconButton
                    onClick={handleClickClose}
                    className="absolute right-4 top-4 size-8 rounded-[50%] bg-transparent text-[#4B5563] transition-transform hover:scale-110 focus:bg-transparent focus:ring-0 focus-visible:outline-none active:scale-100 active:bg-transparent active:text-[#4B5563]"
                  >
                    <CloseIcon className="size-6" />
                  </IconButton>
                </Dialog.Close>
              </div>
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </React.Suspense>
    </Dialog.Root>
  )
}
